/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { Dispatch, useReducer } from 'react';

import {
   MedicalDeviceOrder,
   MedicalDeviceOrderError,
   MedicalDeviceOrderLastUpdatedQuery,
   MedicalDeviceOrderPrintCommand,
   MedicalDeviceOrderPrintResult,
   MedicalDeviceOrderQuery,
} from '@dreryk/egabinet-ezwm-frontend-api';

import {
   useCqrsCommand,
   UseCqrsCommandResult,
   useCqrsFind,
   UseCqrsQueryResult,
} from '@dreryk/command-query-react-common';
import { assertUnreachable, printDocumentAsPdf } from '@dreryk/react-common';
import { useNavTabsContext } from '@dreryk/react-components';

import { visitPaths } from '~libs.desktop.shared.paths';
import { CertificateFault, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';

import {
   ExtendedByVisitDocumentExternalId,
   ExternalId,
} from '../../../common/visitDocument/VisitDocumentContext.types';

import { MedicalDeviceOrderAllFieldsFormBean } from './medicalDeviceOrder.types';
import { MedicalDeviceOrderEzwmAuthenticationFault } from './MedicalDeviceOrderEzwmLoginErrorsModal';
import { mapFormBeanToMedicalDeviceOrderDetails } from './medicalDeviceOrder.utils';

export const useMedicalDeviceOrderQuery = (
   externalId: ExternalId | undefined
): UseCqrsQueryResult<MedicalDeviceOrder | null> => {
   return useCqrsFind<MedicalDeviceOrder | null>(
      () => {
         const query = new MedicalDeviceOrderQuery();
         if (externalId) {
            query.externalId = externalId;
         }
         return query;
      },
      {
         enabled: Boolean(externalId),
      }
   );
};

export const useLastUpdatedMedicalDeviceOrderQuery = (
   visitId: number,
   enabled: boolean
): UseCqrsQueryResult<MedicalDeviceOrder | null> => {
   return useCqrsFind<MedicalDeviceOrder | null>(
      () => {
         const medicalDeviceOrderDraftQuery = new MedicalDeviceOrderLastUpdatedQuery();
         medicalDeviceOrderDraftQuery.visitId = visitId;
         return medicalDeviceOrderDraftQuery;
      },
      {
         enabled: enabled,
      }
   );
};

type UseMedicalDeviceOrderCommandOptions = {
   onSuccess: VoidFunction;
   onAuthenticationRequiredFault: VoidFunction;
};

export const useMedicalDeviceOrderPrintCommand = (
   options: UseMedicalDeviceOrderCommandOptions
): UseCqrsCommandResult<
   MedicalDeviceOrderPrintResult,
   ExtendedByVisitDocumentExternalId<MedicalDeviceOrderAllFieldsFormBean>,
   CertificateFault
> => {
   const { changeTab } = useNavTabsContext<visitPaths.MedicalDevicesPanelNavParams>();
   const { onAuthenticationRequiredFault } = options;
   const certificateFaultHandlers = useCertificateFaultHandlerMap({
      onAuthenticationRequired: onAuthenticationRequiredFault,
   });
   return useCqrsCommand(
      ({ data: formData, externalId }) => {
         const command = new MedicalDeviceOrderPrintCommand();
         command.externalId = externalId;
         command.details = mapFormBeanToMedicalDeviceOrderDetails(formData);
         return command;
      },
      {
         onSuccess: async (result) => {
            options.onSuccess();
            await printDocumentAsPdf({ base64: result.document });
            changeTab(visitPaths.MedicalDevicesPanelNavParams.ORDERS_HISTORY);
         },
         onFault: { ...certificateFaultHandlers },
      }
   );
};

type EzwmLoginErrorState = {
   ezwmAuthenticationFault: MedicalDeviceOrderEzwmAuthenticationFault | null;
};
type DocumentVerificationErrorsState = {
   documentVerificationErrors: MedicalDeviceOrderError[];
};
type EmptyErrorsState = { ezwmAuthenticationFault: null; documentVerificationErrors: [] };
type ErrorsState =
   | (EzwmLoginErrorState & { documentVerificationErrors: [] })
   | (DocumentVerificationErrorsState & { ezwmAuthenticationFault: null })
   | EmptyErrorsState;
const INITIAL_ERRORS_STATE: EmptyErrorsState = {
   ezwmAuthenticationFault: null,
   documentVerificationErrors: [],
};
export enum MedicalDeviceErrorsActionType {
   SET_LOGIN_ERROR,
   SET_DOCUMENT_VERIFICATION_ERRORS,
}
type SetAuthenticationFaultAction = {
   type: MedicalDeviceErrorsActionType.SET_LOGIN_ERROR;
   ezwmAuthenticationFault: MedicalDeviceOrderEzwmAuthenticationFault | null;
};
type SetDocumentVerificationErrorsAction = {
   type: MedicalDeviceErrorsActionType.SET_DOCUMENT_VERIFICATION_ERRORS;
   documentVerificationErrors: MedicalDeviceOrderError[];
};
const errorsStateReducer = (
   state: ErrorsState,
   action: SetAuthenticationFaultAction | SetDocumentVerificationErrorsAction
): ErrorsState => {
   switch (action.type) {
      case MedicalDeviceErrorsActionType.SET_LOGIN_ERROR: {
         return {
            ...state,
            ezwmAuthenticationFault: action.ezwmAuthenticationFault,
            documentVerificationErrors: [],
         };
      }
      case MedicalDeviceErrorsActionType.SET_DOCUMENT_VERIFICATION_ERRORS: {
         return {
            ...state,
            documentVerificationErrors: action.documentVerificationErrors,
            ezwmAuthenticationFault: null,
         };
      }
      default:
         return assertUnreachable(action);
   }
};
export const useErrorsReducer = (
   existingDocumentValidationErrors: MedicalDeviceOrderError[]
): [ErrorsState, Dispatch<SetAuthenticationFaultAction | SetDocumentVerificationErrorsAction>] => {
   return useReducer(errorsStateReducer, {
      ...INITIAL_ERRORS_STATE,
      documentVerificationErrors: existingDocumentValidationErrors,
   });
};
